// ─────────────────────────────────────────────────────────────────────────
// KinsCircle — Invited Member Self-Heal & Family Reconnection Helpers
//
// LEGACY DATA POLICY: see LEGACY_DATA_POLICY.md.
// STABILITY CHARTER:  see STABILITY.md §8 (Invite + Recovery + Reconnection Lock).
//
// Purpose
// -------
// When an authenticated user lands on the dashboard but one of the
// downstream identity rows is missing or stale — profile, family
// membership, viewerMemberId, accepted-invite mapping, or
// family_graph_members anchor — the app must NEVER blank-screen,
// orphan, or trap them in incomplete onboarding. Instead it should
// attempt a non-destructive, idempotent self-heal that reconnects
// the user to the inviter's family graph (and only that graph), and
// fall back cleanly to read-only mode + the AppRecovery surface
// when repair is not safe.
//
// This module is PURE ADDITIVE INFRASTRUCTURE — it does NOT mutate
// the spouse / in-law engine, the tree layout, the relationship
// labels, the Supabase schema, or any existing family graph row.
// Every operation is read-only OR a SAFE upsert that respects
// existing data.
//
// Hard prohibitions (enforced by every helper below)
// --------------------------------------------------
//   - NEVER delete family_graph_members, family_graph_photos,
//     family_graph_memories, comments, invites, parent_ids, spouse_id,
//     or relationship_override.
//   - NEVER overwrite a non-null claimed_by_user_id with a different
//     user_id. Existing claims are sacred.
//   - NEVER write the inviter's tree into a different family graph.
//   - NEVER auto-promote a member to owner / admin.
//   - NEVER fabricate a paid plan; subscription state is read-only here.
//
// Failure mode
// ------------
// Every helper returns a structured result. On unrecoverable failure
// the caller should render <ReadOnlyAccessBanner /> or route through
// <AppRecovery /> — never crash, never silently succeed.
// ─────────────────────────────────────────────────────────────────────────

import { supabase } from '@/lib/supabase';
import { getInviteContext } from '@/lib/inviteContext';
import { logInviteAcceptanceFailure } from '@/lib/stabilityLogger';

export interface SelfHealResult {
  ok: boolean;
  /** Stable token the UI can show in support-toast subtext. */
  correlation: string;
  /** Internal reason — never shown to end users verbatim. */
  reason?: string;
  /** Inviter family-graph owner user id we reconnected the user to (if any). */
  inviterUserId?: string | null;
  /** Steps the helper actually performed (audit trail for /admin/health). */
  steps: string[];
}

const newCorrelation = () =>
  `selfheal-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * Read-only diagnosis: identifies which identity rows are missing for
 * the currently authenticated user. Performs ZERO writes. Safe to call
 * on every dashboard mount.
 */
export async function diagnoseInvitedMember(opts: {
  authUserId: string;
  authEmail?: string | null;
}): Promise<{
  hasProfileRow: boolean;
  hasGraphMembership: boolean;
  hasViewerMember: boolean;
  hasPendingInvite: boolean;
  inviterUserId: string | null;
  inviteToken: string | null;
  inviteIsPersonTargeted: boolean;
}> {
  const { authUserId, authEmail } = opts;

  let hasProfileRow = false;
  let hasGraphMembership = false;
  let hasViewerMember = false;
  let hasPendingInvite = false;
  let inviteIsPersonTargeted = false;

  try {
    const { data: prof } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', authUserId)
      .maybeSingle();
    hasProfileRow = !!prof?.id;
  } catch {
    /* read-only — never throws */
  }

  try {
    const { data: viewer } = await supabase
      .from('family_graph_members')
      .select('id')
      .eq('claimed_by_user_id', authUserId)
      .is('deleted_at', null)
      .limit(1)
      .maybeSingle();
    hasViewerMember = !!viewer?.id;
    hasGraphMembership = hasViewerMember;
  } catch {
    /* read-only */
  }

  // Check invite context from localStorage / sessionStorage / URL.
  const ctx = getInviteContext();
  const inviterUserId = ctx?.inviterId ?? null;
  const inviteToken = ctx?.token ?? null;

  if (inviterUserId) {
    try {
      const orParts: string[] = [];
      if (inviteToken) orParts.push(`token.eq.${inviteToken}`);
      if (authEmail) orParts.push(`recipient_email.eq.${authEmail}`);
      if (orParts.length > 0) {
        const { data: inv } = await supabase
          .from('invites')
          .select('id, accepted_user_id, target_member_id')
          .or(orParts.join(','))
          .limit(1)
          .maybeSingle();
        hasPendingInvite = !!inv?.id && !inv.accepted_user_id;
        inviteIsPersonTargeted = Boolean(inv?.target_member_id);
      }
    } catch {
      /* read-only */
    }
  }

  return {
    hasProfileRow,
    hasGraphMembership,
    hasViewerMember,
    hasPendingInvite,
    inviterUserId,
    inviteToken,
    inviteIsPersonTargeted,
  };
}

/**
 * Idempotent profile-row self-heal. NEVER touches plan or role.
 */
export async function ensureProfileRow(opts: {
  authUserId: string;
  authEmail?: string | null;
}): Promise<{ ok: boolean; reason?: string }> {
  try {
    const { error } = await supabase
      .from('profiles')
      .insert({
        id: opts.authUserId,
        email: opts.authEmail ?? null,
        plan: 'free',
        role: 'member',
      });
    if (!error) return { ok: true };
    const msg = String(error.message || '');
    if (/duplicate key|already exists|23505/i.test(msg)) {
      return { ok: true };
    }
    return { ok: false, reason: msg };
  } catch (err: any) {
    return { ok: false, reason: err?.message || 'profile insert threw' };
  }
}

/**
 * Top-level self-heal entrypoint.
 *
 * Strategy (each step is independently safe and idempotent):
 *   1. Read invite context (localStorage / sessionStorage / URL).
 *   2. Ensure profiles row exists for the auth user.
 *   3. Mark the matching invite row as accepted_at + accepted_user_id
 *      (only when the row isn't already accepted by another user).
 *   4. Return inviterUserId so the caller can re-trigger the existing
 *      RelationshipOnboardingModal binding flow (which is the only
 *      code path allowed to write into family_graph_members).
 */
export async function attemptInvitedMemberSelfHeal(opts: {
  authUserId: string;
  authEmail?: string | null;
}): Promise<SelfHealResult> {
  const correlation = newCorrelation();
  const steps: string[] = [];

  try {
    const diag = await diagnoseInvitedMember({
      authUserId: opts.authUserId,
      authEmail: opts.authEmail,
    });
    steps.push(
      `diagnose:${JSON.stringify({
        hasProfileRow: diag.hasProfileRow,
        hasGraphMembership: diag.hasGraphMembership,
        hasViewerMember: diag.hasViewerMember,
        hasPendingInvite: diag.hasPendingInvite,
        inviterUserId: diag.inviterUserId ? 'present' : 'absent',
      })}`,
    );

    // Step 2: profile row.
    if (!diag.hasProfileRow) {
      const r = await ensureProfileRow({
        authUserId: opts.authUserId,
        authEmail: opts.authEmail,
      });
      steps.push(`ensureProfileRow:${r.ok ? 'ok' : `fail(${r.reason})`}`);
      if (!r.ok) {
        const corr = logInviteAcceptanceFailure({
          message: 'profile-row-create-failed',
          context: { authUserId: opts.authUserId, reason: r.reason ?? null },
        });
        return {
          ok: false,
          correlation: corr || correlation,
          reason: r.reason,
          inviterUserId: diag.inviterUserId,
          steps,
        };
      }
    }

    // Step 3: stamp the invite as accepted (best-effort, token-scoped).
    if (
      diag.inviteToken &&
      !diag.inviteIsPersonTargeted
    ) {
      try {
        const { error } = await supabase
          .from('invites')
          .update({
            status: 'accepted',
            accepted_at: new Date().toISOString(),
            accepted_user_id: opts.authUserId,
          })
          .eq('token', diag.inviteToken)
          .is('accepted_user_id', null);
        steps.push(
          `stampInviteAccepted:${error ? `skip(${error.message})` : 'ok'}`,
        );
      } catch {
        steps.push('stampInviteAccepted:exception-ignored');
      }
    }

    // Family-graph membership row creation is INTENTIONALLY left to
    // the existing RelationshipOnboardingModal pipeline. The caller
    // should re-open that modal (or re-run resolvePendingInvite()) now
    // that the profile + invite rows are coherent.
    return {
      ok: true,
      correlation,
      inviterUserId: diag.inviterUserId,
      steps,
    };
  } catch (err: any) {
    const corr = logInviteAcceptanceFailure({
      message: 'selfheal-exception',
      cause: err,
      context: { authUserId: opts.authUserId },
    });
    return {
      ok: false,
      correlation: corr || correlation,
      reason: err?.message || 'self-heal threw',
      steps,
    };
  }
}
